"use client";

import {
  createChart,
  IChartApi,
  ISeriesApi,
  type MouseEventParams,
  type HistogramData,
  type CandlestickData,
  type Time,
} from "lightweight-charts";
import { useCallback, useEffect, useRef, useState } from "react";

import type { CandleData } from "@/hooks/useTradeData";
import type { IndicatorConfig } from "@/stores/tradeStore";

interface PriceChartProps {
  candles: CandleData[];
  indicators: IndicatorConfig[];
  loading?: boolean;
  onReady?: (chart: IChartApi) => void;
  isFullScreen?: boolean;
  onExitFullScreen?: () => void;
}

export const PriceChart = ({
  candles,
  indicators,
  loading,
  onReady,
  isFullScreen,
  onExitFullScreen,
}: PriceChartProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const indicatorSeries = useRef<Record<string, ISeriesApi<"Line">>>({});
  const prevCandlesRef = useRef<CandleData[]>([]);
  const [hoverInfo, setHoverInfo] = useState<{
    time?: number;
    open?: number;
    high?: number;
    low?: number;
    close?: number;
    volume?: number;
  }>({});

  const resizeChart = useCallback(() => {
    if (!chartRef.current || !containerRef.current) return;
    chartRef.current.applyOptions({
      width: containerRef.current.clientWidth,
      height: isFullScreen ? Math.max(520, Math.floor(window.innerHeight * 0.9)) : 520,
    });
  }, [isFullScreen]);

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: isFullScreen ? Math.max(520, Math.floor(window.innerHeight * 0.9)) : 520,
      layout: { background: { color: "transparent" }, textColor: "#f8fafc" },
      rightPriceScale: {
        borderColor: "#e2e8f0",
        scaleMargins: { top: 0.05, bottom: 0.25 },
      },
      timeScale: { borderColor: "#e2e8f0", timeVisible: true, secondsVisible: false },
      grid: {
        vertLines: { color: "#e2e8f0" },
        horzLines: { color: "#e2e8f0" },
      },
      crosshair: { mode: 0 },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: "#22c55e",
      downColor: "#ef4444",
      borderVisible: false,
      wickUpColor: "#22c55e",
      wickDownColor: "#ef4444",
    });

    const volumeSeries = chart.addHistogramSeries({
      color: "#cbd5e1",
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
    });

    chart.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.75, bottom: 0.02 },
      borderColor: "#e2e8f0",
    });

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;

    const handleResize = () => {
      resizeChart();
    };
    window.addEventListener("resize", handleResize);

    const handleCrosshair = (param: MouseEventParams) => {
      if (!volumeSeriesRef.current) return;
      const candlePoint = param?.seriesData.get(candleSeries) as
        | CandlestickData
        | undefined;
      const volumePoint = param?.seriesData.get(volumeSeries) as
        | HistogramData
        | undefined;

      if (candlePoint) {
        setHoverInfo({
          time: Number(param?.time) || undefined,
          open: candlePoint.open,
          high: candlePoint.high,
          low: candlePoint.low,
          close: candlePoint.close,
          volume: volumePoint?.value,
        });
      } else if (candles.length) {
        const last = candles[candles.length - 1];
        setHoverInfo({
          time: last.time,
          open: last.open,
          high: last.high,
          low: last.low,
          close: last.close,
          volume: last.volume,
        });
      }
    };
    chart.subscribeCrosshairMove(handleCrosshair);

    onReady?.(chart);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.unsubscribeCrosshairMove(handleCrosshair);
      chart.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!chartRef.current || !el) return;

    if (isFullScreen) {
      if (el.requestFullscreen) {
        el.requestFullscreen()
          .then(() => setTimeout(() => resizeChart(), 10))
          .catch(() => resizeChart());
      } else {
        resizeChart();
      }
    } else if (document.fullscreenElement === el && document.exitFullscreen) {
      document.exitFullscreen().finally(() => setTimeout(() => resizeChart(), 10));
    } else {
      resizeChart();
    }
  }, [isFullScreen, resizeChart]);

  useEffect(() => {
    const onChange = () => {
      const active = document.fullscreenElement === containerRef.current;
      if (!active && isFullScreen) {
        onExitFullScreen?.();
      }
      setTimeout(() => resizeChart(), 20);
    };
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, [isFullScreen, onExitFullScreen, resizeChart]);

  useEffect(() => {
    if (!candleSeriesRef.current || !volumeSeriesRef.current || !candles?.length) {
      console.log("[PriceChart] Skipping - no series or candles");
      return;
    }

    console.log("[PriceChart] useEffect triggered, candles count:", candles.length);

    // Sort by time ascending and deduplicate (keep last entry for duplicate timestamps)
    const sortedCandles = [...candles]
      .sort((a, b) => a.time - b.time)
      .reduce<CandleData[]>((acc, candle) => {
        const lastCandle = acc[acc.length - 1];
        if (lastCandle && lastCandle.time === candle.time) {
          // Replace duplicate timestamp with newer data
          acc[acc.length - 1] = candle;
        } else {
          acc.push(candle);
        }
        return acc;
      }, []);

    if (!sortedCandles.length) return;

    const prevCandles = prevCandlesRef.current;
    const isRealTimeUpdate =
      prevCandles.length > 0 &&
      sortedCandles.length >= prevCandles.length &&
      sortedCandles.length <= prevCandles.length + 1;

    console.log("[PriceChart] Update check:", {
      prevLength: prevCandles.length,
      currentLength: sortedCandles.length,
      isRealTimeUpdate,
      lastCandleClose: sortedCandles[sortedCandles.length - 1]?.close,
    });

    if (isRealTimeUpdate && sortedCandles.length > 0) {
      // Real-time update: use update() for efficiency
      const lastCandle = sortedCandles[sortedCandles.length - 1];
      console.log("[PriceChart] Calling update() with:", {
        time: lastCandle.time,
        close: lastCandle.close,
      });
      candleSeriesRef.current.update({
        time: lastCandle.time as Time,
        open: lastCandle.open,
        high: lastCandle.high,
        low: lastCandle.low,
        close: lastCandle.close,
      });
      volumeSeriesRef.current.update({
        time: lastCandle.time as Time,
        value: lastCandle.volume,
        color:
          lastCandle.close >= lastCandle.open
            ? "rgba(34,197,94,0.4)"
            : "rgba(239,68,68,0.4)",
      });
    } else {
      // Full data replacement (initial load or timeframe change)
      candleSeriesRef.current.setData(
        sortedCandles.map((c) => ({
          time: c.time as Time,
          open: c.open,
          high: c.high,
          low: c.low,
          close: c.close,
        }))
      );

      volumeSeriesRef.current.setData(
        sortedCandles.map((c) => ({
          time: c.time as Time,
          value: c.volume,
          color: c.close >= c.open ? "rgba(34,197,94,0.4)" : "rgba(239,68,68,0.4)",
        }))
      );
    }

    // Store for next comparison
    prevCandlesRef.current = sortedCandles;

    const last = sortedCandles[sortedCandles.length - 1];
    candleSeriesRef.current.setMarkers([
      {
        time: last.time as Time,
        position: "aboveBar",
        shape: "circle",
        color: "#f8fafc",
        text: last.close.toFixed(2),
      },
    ]);

    setHoverInfo({
      time: last.time,
      open: last.open,
      high: last.high,
      low: last.low,
      close: last.close,
      volume: last.volume,
    });
  }, [candles]);

  useEffect(() => {
    if (!chartRef.current || !candles.length) return;
    Object.values(indicatorSeries.current).forEach((series) => {
      chartRef.current?.removeSeries(series);
    });
    indicatorSeries.current = {};

    // Sort and deduplicate for indicators too
    const sortedCandles = [...candles]
      .sort((a, b) => a.time - b.time)
      .reduce<CandleData[]>((acc, candle) => {
        const lastCandle = acc[acc.length - 1];
        if (lastCandle && lastCandle.time === candle.time) {
          acc[acc.length - 1] = candle;
        } else {
          acc.push(candle);
        }
        return acc;
      }, []);

    indicators
      .filter((ind) => ind.visible)
      .forEach((ind) => {
        const series = chartRef.current!.addLineSeries({
          color: ind.color,
          lineWidth: 2,
        });
        const values = calculateIndicator(sortedCandles, ind);
        series.setData(values);
        indicatorSeries.current[ind.id] = series;
      });
  }, [indicators, candles]);

  return (
    <div
      className={`relative rounded-2xl border border-slate-200 bg-transparent p-3 shadow-sm ${
        isFullScreen ? "fixed inset-0 z-50 bg-slate-950/80 backdrop-blur" : ""
      }`}
      style={isFullScreen ? { height: "100vh" } : undefined}
    >
      <div className="mb-2 flex flex-wrap items-center gap-3 text-xs font-semibold text-slate-100">
        <span>O: {hoverInfo.open?.toFixed(2) ?? "--"}</span>
        <span>H: {hoverInfo.high?.toFixed(2) ?? "--"}</span>
        <span>L: {hoverInfo.low?.toFixed(2) ?? "--"}</span>
        <span>C: {hoverInfo.close?.toFixed(2) ?? "--"}</span>
        <span className="text-slate-300">
          Vol: {hoverInfo.volume?.toFixed(0) ?? "--"}
        </span>
      </div>
      {loading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 text-sm font-semibold text-slate-600">
          Loading chart...
        </div>
      ) : null}
      <div ref={containerRef} data-testid="price-chart" />
    </div>
  );
};

const calculateIndicator = (candles: CandleData[], ind: IndicatorConfig) => {
  switch (ind.type) {
    case "SMA":
      return simpleMA(candles, ind.period);
    case "EMA":
      return exponentialMA(candles, ind.period);
    default:
      return [];
  }
};

const simpleMA = (candles: CandleData[], period: number) => {
  const result: { time: Time; value: number }[] = [];
  for (let i = 0; i < candles.length; i++) {
    if (i + 1 < period) continue;
    const slice = candles.slice(i + 1 - period, i + 1);
    const avg = slice.reduce((sum, c) => sum + c.close, 0) / period;
    result.push({ time: candles[i].time as Time, value: avg });
  }
  return result;
};

const exponentialMA = (candles: CandleData[], period: number) => {
  const result: { time: Time; value: number }[] = [];
  const k = 2 / (period + 1);
  let prevEma = candles[0]?.close ?? 0;
  candles.forEach((candle, idx) => {
    const ema = idx === 0 ? candle.close : candle.close * k + prevEma * (1 - k);
    prevEma = ema;
    if (idx + 1 >= period) {
      result.push({ time: candle.time as Time, value: ema });
    }
  });
  return result;
};
